import { useState } from 'react';
import type { PaqueteWhatsApp } from '../../api/ops/plan';
import { ErrorApi } from '../../api/problema';
import { useAvisoBreve } from '../../componentes/avisoBreve';
import { Boton } from '../../componentes/Boton';
import { Chip } from '../../componentes/Chip';
import { Desplegable } from '../../componentes/Desplegable';
import { Icono } from '../../componentes/Icono';
import { Ventana } from '../../componentes/Ventana';
import { miles } from './logica';
import {
    AYUDA_RECARGA,
    PAQUETES_RECARGA,
    SOLO_PROPIETARIO,
    TAMANOS,
    UMBRALES,
    avisoFallo,
    borradorInicial,
    codigoDeTamano,
    filaRecarga,
    textosQuitar,
    textosVentana,
    type PrecioDe,
} from './recarga';

type Cuerpo = { enabled: boolean; product_code?: string | null; below_messages?: number | null };

interface Props {
    paquetes: readonly PaqueteWhatsApp[];
    movil: boolean;
    /**
     * El propietario (`plan.consumo.comprar`) con plan contratado y fuera de solo lectura: activa, cambia o quita la recarga
     * sin esperar a `cobroDisponible()` (G1416); el resto solo ve (dibujo 1.16).
     */
    propietario: boolean;
    /**
     * «Cambiar el método de pago» (el portal de Stripe) sí espera al cobro disponible (`cobroDisponible()`, G1427: en pre sí;
     * en producción, tras G92).
     */
    cobro: boolean;
    precioDe: PrecioDe;
    /** La tarjeta guardada («Visa •••• 4242», de `Subscription.payment_method`, G1415); sin ella, «la tarjeta guardada». */
    tarjeta: string | null;
    guardar: (pack: PaqueteWhatsApp['pack'], cuerpo: Cuerpo) => Promise<unknown>;
    onMetodoPago: () => void;
    onCambio: () => void;
}

type Abierta = { pack: PaqueteWhatsApp['pack']; modo: 'editar' | 'quitar'; hecho: { titulo: string; texto: string } | null } | null;

/**
 * «Recarga automática» de Consumo (1.16; T.1, G1237, G1239, G1241, G1242, G1244): una fila por paquete con su estado,
 * «Activar», «Cambiar» y «Quitar» (solo el propietario con plan contratado, G1416) y la ventana del umbral y el tamaño.
 */
export function RecargaAutomatica({ paquetes, movil, propietario, cobro, precioDe, tarjeta, guardar, onMetodoPago, onCambio }: Props) {
    const avisar = useAvisoBreve();
    const [abierta, setAbierta] = useState<Abierta>(null);
    const [borrador, setBorrador] = useState({ umbral: 200, tamano: 1000 });
    const [ocupado, setOcupado] = useState(false);
    const [reactivando, setReactivando] = useState<PaqueteWhatsApp['pack'] | null>(null);
    const puede = propietario;
    const filas = PAQUETES_RECARGA.flatMap(({ pack, icono }) => {
        const p = paquetes.find((x) => x.pack === pack);
        return p === undefined ? [] : [{ p, f: filaRecarga(p, icono, precioDe) }];
    });
    if (filas.length === 0) {
        return null;
    }
    const abrir = (p: PaqueteWhatsApp, modo: 'editar' | 'quitar') => {
        setBorrador(borradorInicial(p));
        setAbierta({ pack: p.pack, modo, hecho: null });
    };
    // G1408: «Volver a activar» es de un clic, con el tamaño y el umbral que se conservaron al apagarse; sin ellos, la ventana.
    const reactivar = (p: PaqueteWhatsApp) => {
        const { product_code: producto, below_messages: umbral } = p.auto_top_up;
        if (producto == null || umbral == null) {
            abrir(p, 'editar');
            return;
        }
        setReactivando(p.pack);
        guardar(p.pack, { enabled: true, product_code: producto, below_messages: umbral })
            .then(() => {
                avisar({ tipo: 'ok', texto: 'Recarga automática activada' });
                onCambio();
            })
            .catch((e: unknown) => {
                avisar({ tipo: 'error', texto: e instanceof ErrorApi ? e.message : 'No he podido activarla. Prueba de nuevo.' });
            })
            .finally(() => {
                setReactivando(null);
            });
    };
    const actual = abierta === null ? undefined : paquetes.find((x) => x.pack === abierta.pack);
    const textos = actual === undefined ? null : textosVentana(actual, borrador.umbral, borrador.tamano, precioDe, tarjeta);
    const quitar = actual === undefined ? null : textosQuitar(actual);

    const confirmar = () => {
        if (abierta === null || actual === undefined || textos === null || quitar === null) {
            return;
        }
        if (abierta.hecho !== null) {
            setAbierta(null);
            return;
        }
        const cuerpo: Cuerpo =
            abierta.modo === 'quitar'
                ? { enabled: false, product_code: actual.auto_top_up.product_code ?? null, below_messages: actual.auto_top_up.below_messages ?? null }
                : { enabled: true, product_code: codigoDeTamano(actual.pack, borrador.tamano), below_messages: borrador.umbral };
        setOcupado(true);
        guardar(actual.pack, cuerpo)
            .then(() => {
                setAbierta({ ...abierta, hecho: abierta.modo === 'quitar' ? quitar.hecho : textos.hecho });
                onCambio();
            })
            .catch((e: unknown) => {
                avisar({ tipo: 'error', texto: e instanceof ErrorApi ? e.message : 'No he podido guardarlo. Prueba de nuevo.' });
            })
            .finally(() => {
                setOcupado(false);
            });
    };

    return (
        <section className="rv-plan__seccion">
            <div className="rv-plan__titulo">
                <h3>Recarga automática</h3>
                <span>Paquetes de WhatsApp · con la tarjeta guardada</span>
            </div>
            <p className="rv-plan__nota-recarga">{AYUDA_RECARGA}</p>
            <div className="rv-plan__bloques">
                {filas.map(({ p, f }) => (
                    <div key={f.pack} className={`rv-plan__recarga${movil ? ' rv-plan__recarga--movil' : ''}`}>
                        <div className="rv-plan__recarga-fila">
                            <span className={`rv-plan__baldosa${f.activa ? '' : ' rv-plan__baldosa--apagada'}`} aria-hidden="true">
                                <Icono nombre={f.icono} />
                            </span>
                            <span className="rv-plan__bloque-textos">
                                <span className="rv-plan__recarga-nombre">
                                    <strong>{f.nombre}</strong>
                                    <Chip estado={f.chip.estado}>{f.chip.texto}</Chip>
                                </span>
                                <span>{f.linea}</span>
                            </span>
                            {puede && f.fallo === null && (
                                <span className="rv-plan__recarga-botones">
                                    {f.activa ? (
                                        <>
                                            <Boton tipo="secundario" icono="pencil" movil={movil} onClick={() => { abrir(p, 'editar'); }}>Cambiar</Boton>
                                            <Boton tipo="secundario" icono="power" movil={movil} onClick={() => { abrir(p, 'quitar'); }}>Quitar</Boton>
                                        </>
                                    ) : (
                                        <Boton tipo="secundario" icono="refresh-cw" movil={movil} onClick={() => { abrir(p, 'editar'); }}>Activar</Boton>
                                    )}
                                </span>
                            )}
                        </div>
                        {f.fallo !== null && (
                            // G1239, G1407: apagada tras un cobro fallido, con cuándo.
                            <div className="rv-plan__recarga-fallo" role="alert">
                                <Icono nombre="circle-alert" />
                                <span className="rv-plan__bloque-textos">
                                    <strong>{avisoFallo(f.fallo).titulo}</strong>
                                    <span>{avisoFallo(f.fallo).texto}</span>
                                </span>
                                {/* Si falló un cobro, el plan tiene tarjeta guardada: reactivar (G1408) no espera al cobro del panel (G92). */}
                                {propietario && (
                                    <span className="rv-plan__recarga-botones">
                                        <Boton icono="refresh-cw" movil={movil} ocupado={reactivando === p.pack} onClick={() => { reactivar(p); }}>Volver a activar</Boton>
                                        {cobro && <Boton tipo="secundario" icono="credit-card" movil={movil} onClick={onMetodoPago}>Cambiar el método de pago</Boton>}
                                    </span>
                                )}
                            </div>
                        )}
                    </div>
                ))}
            </div>
            {!propietario && (
                <p className="rv-plan__aviso">
                    <Icono nombre="lock" />
                    <span>{SOLO_PROPIETARIO}</span>
                </p>
            )}
            {abierta !== null && actual !== undefined && textos !== null && quitar !== null && (
                <Ventana
                    abierta
                    movil={movil}
                    ancho="480px"
                    titulo={abierta.hecho?.titulo ?? (abierta.modo === 'quitar' ? quitar.titulo : textos.titulo)}
                    texto={abierta.hecho?.texto ?? (abierta.modo === 'quitar' ? quitar.texto : textos.texto)}
                    cancelar={abierta.hecho !== null ? '' : abierta.modo === 'quitar' ? 'Dejarla activa' : 'Cancelar'}
                    cancelarIcono={abierta.modo === 'quitar' ? 'arrow-left' : 'x'}
                    accion={abierta.hecho !== null ? 'Hecho' : abierta.modo === 'quitar' ? 'Quitar la recarga' : textos.accion}
                    accionIcono={abierta.hecho !== null ? 'check' : abierta.modo === 'quitar' ? 'power' : 'refresh-cw'}
                    ocupado={ocupado}
                    onAccion={confirmar}
                    onCerrar={() => { if (!ocupado) { setAbierta(null); } }}
                >
                    {abierta.hecho === null && abierta.modo === 'editar' && (
                        <div className="rv-plan__recarga-campos">
                            <Desplegable
                                etiqueta="Cuando queden menos de"
                                valor={String(borrador.umbral)}
                                opciones={UMBRALES.map((n) => ({ valor: String(n), texto: `${miles(n)} mensajes` }))}
                                onChange={(v) => { setBorrador({ ...borrador, umbral: Number(v) }); }}
                            />
                            <Desplegable
                                etiqueta="Comprar"
                                valor={String(borrador.tamano)}
                                opciones={TAMANOS.map((n) => {
                                    const precio = precioDe(codigoDeTamano(actual.pack, n));
                                    return { valor: String(n), texto: `${miles(n)} mensajes${precio === null ? '' : ` · ${precio}`}` };
                                })}
                                ayuda={textos.ayudaTamano}
                                onChange={(v) => { setBorrador({ ...borrador, tamano: Number(v) }); }}
                            />
                            {textos.pequeno !== null && (
                                <p className="rv-plan__recarga-ojo" role="status">
                                    <Icono nombre="triangle-alert" />
                                    <span>{textos.pequeno}</span>
                                </p>
                            )}
                            {textos.yaBajo !== null && (
                                <p className="rv-plan__aviso">
                                    <Icono nombre="info" />
                                    <span>{textos.yaBajo}</span>
                                </p>
                            )}
                        </div>
                    )}
                </Ventana>
            )}
        </section>
    );
}
